import { useState, useEffect } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Button from "../button/Button";
import styles from "./DateRangeFilter.module.scss";
import { AggregationType } from "../../../../model/transaction";

interface DateRangeFilterProps {
	isOpen: boolean;
	onClose: () => void;
	onApply: (from: Date | null, to: Date | null, aggregateByOpion: AggregationType | null) => void;
	initialFrom: Date | null;
	initialTo: Date | null;
	initialAggregateBy?: AggregationType;
	showAggregateByOptions?: boolean | null;
}

type PresetOption = "today" | "yesterday" | "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth" | null;

export const DateRangeFilter = ({ isOpen, onClose, onApply, initialFrom, initialTo, initialAggregateBy, showAggregateByOptions }: DateRangeFilterProps) => {
	const [selectedPreset, setSelectedPreset] = useState<PresetOption>("thisMonth");
	const [aggregateOption, setAggregateOption] = useState<AggregationType>(AggregationType.DAY);

	const [dateRange, setDateRange] = useState([
		{
			startDate: initialFrom || getThisMonthStart(),
			endDate: initialTo || getThisMonthEnd(),
			key: "selection",
		},
	]);

	useEffect(() => {
		// Sync internal state with props when modal opens
		if (isOpen) {
			const start = initialFrom || getThisMonthStart();
			const end = initialTo || getThisMonthEnd();
			setDateRange([{ startDate: start, endDate: end, key: "selection" }]);
			setAggregateOption(initialAggregateBy ?? AggregationType.DAY);
			// Set preset to "thisMonth" if dates match current month
			if (!initialFrom && !initialTo) {
				setSelectedPreset("thisMonth");
			} else {
				setSelectedPreset(null);
			}
		}
	}, [isOpen, initialFrom, initialTo, initialAggregateBy]);

	function getThisMonthStart(): Date {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), 1);
	}

	function getThisMonthEnd(): Date {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth() + 1, 0);
	}

	function getLastMonthStart(): Date {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth() - 1, 1);
	}

	function getLastMonthEnd(): Date {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), 0);
	}

	function getToday(): Date {
		return new Date();
	}

	function getYesterday(): Date {
		const date = new Date();
		date.setDate(date.getDate() - 1);
		return date;
	}

	function getThisWeekStart(): Date {
		const now = new Date();
		const dayOfWeek = now.getDay();
		const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Start on Monday
		const monday = new Date(now);
		monday.setDate(now.getDate() + diff);
		return monday;
	}

	function getLastWeekStart(): Date {
		const thisWeekStart = getThisWeekStart();
		const lastWeekStart = new Date(thisWeekStart);
		lastWeekStart.setDate(thisWeekStart.getDate() - 7);
		return lastWeekStart;
	}

	function getLastWeekEnd(): Date {
		const thisWeekStart = getThisWeekStart();
		const lastWeekEnd = new Date(thisWeekStart);
		lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
		return lastWeekEnd;
	}

	const handlePresetClick = (preset: PresetOption) => {
		setSelectedPreset(preset);
		let start: Date, end: Date;

		switch (preset) {
			case "today": {
				start = getToday();
				end = getToday();
				break;
			}
			case "yesterday": {
				start = getYesterday();
				end = getYesterday();
				break;
			}
			case "thisWeek":
				start = getThisWeekStart();
				end = getToday();
				break;
			case "lastWeek":
				start = getLastWeekStart();
				end = getLastWeekEnd();
				break;
			case "thisMonth":
				start = getThisMonthStart();
				end = getThisMonthEnd();
				break;
			case "lastMonth":
				start = getLastMonthStart();
				end = getLastMonthEnd();
				break;
			default:
				return;
		}

		setDateRange([{ startDate: start, endDate: end, key: "selection" }]);
	};

	const handleReset = () => {
		setSelectedPreset(null);
		const start = getThisMonthStart();
		const end = getThisMonthEnd();
		setDateRange([{ startDate: start, endDate: end, key: "selection" }]);
		onApply(null, null, null);
		onClose();
	};

	const handleApply = () => {
		const { startDate, endDate } = dateRange[0];
		onApply(startDate, endDate, aggregateOption);
		onClose();
	};

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div className={styles.backdrop} onClick={handleBackdropClick}>
			<div className={styles.modal}>
				<div className={styles.header}>
					<h2 className={styles.title}>Select Date Range</h2>
					<button className={styles.closeButton} onClick={onClose}>
						×
					</button>
				</div>

				<div className={styles.content}>
					<div className={styles.presetOptions}>
						<button className={`${styles.presetButton} ${selectedPreset === "today" ? styles.active : ""}`} onClick={() => handlePresetClick("today")}>
							Today
						</button>
						<button className={`${styles.presetButton} ${selectedPreset === "yesterday" ? styles.active : ""}`} onClick={() => handlePresetClick("yesterday")}>
							Yesterday
						</button>
						<button className={`${styles.presetButton} ${selectedPreset === "thisWeek" ? styles.active : ""}`} onClick={() => handlePresetClick("thisWeek")}>
							This Week
						</button>
						<button className={`${styles.presetButton} ${selectedPreset === "lastWeek" ? styles.active : ""}`} onClick={() => handlePresetClick("lastWeek")}>
							Last Week
						</button>
						<button className={`${styles.presetButton} ${selectedPreset === "thisMonth" ? styles.active : ""}`} onClick={() => handlePresetClick("thisMonth")}>
							This Month
						</button>
						<button className={`${styles.presetButton} ${selectedPreset === "lastMonth" ? styles.active : ""}`} onClick={() => handlePresetClick("lastMonth")}>
							Last Month
						</button>
						{showAggregateByOptions && (
							<div className={`${styles.aggregateByDiv}`}>
								<span className={`${styles.aggregateByTitle}`}>Aggregate period:</span>
								<button
									className={`${styles.presetButton} ${aggregateOption === AggregationType.HOUR ? styles.active : ""}`}
									onClick={() => setAggregateOption(AggregationType.HOUR)}
								>
									Hour
								</button>
								<button
									className={`${styles.presetButton} ${aggregateOption === AggregationType.DAY ? styles.active : ""}`}
									onClick={() => setAggregateOption(AggregationType.DAY)}
								>
									Day
								</button>
								<button
									className={`${styles.presetButton} ${aggregateOption === AggregationType.MONTH ? styles.active : ""}`}
									onClick={() => setAggregateOption(AggregationType.MONTH)}
								>
									Month
								</button>
							</div>
						)}
					</div>

					<div className={styles.calendarContainer}>
						<DateRangePicker
							ranges={dateRange}
							onChange={(item: any) => {
								setDateRange([item.selection]);
								setSelectedPreset(null);
							}}
							months={1}
							direction="horizontal"
							rangeColors={["#FF7926"]}
						/>
					</div>
				</div>

				<div className={styles.actions}>
					<Button text="Reset" variant="secondary" onClick={handleReset} />
					<Button text="Apply Filter" variant="primary" onClick={handleApply} />
				</div>
			</div>
		</div>
	);
};
